import { TreeList, Button, Card, CardContent, CardHeader, CardTitle, ScrollArea, Search, Spinner } from '@ibsys/kit';
import { FC, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import {
  useExpertReports,
  useCreateExpertReport,
  useUpdateExpertReport,
  expertReportKeys,
  findPointsRuleOwnerOfChildren,
  flattenBlocks,
  toPredicateInput,
  type ExpertReportResponse,
  type BlockResponse,
} from '@/entities/expert-report';
import { useHasPermission } from '@/entities/profile';
import { usePointsRuleChangeWarning } from '@/features/expert-report/usePointsRuleChangeWarning';
import { useResultsRecalculationNotice } from '@/features/expert-report/useResultsRecalculationNotice';
import { ExpertReportTreeNodeWithData } from './ExpertReportTreeNodeWithData';
import { CreateReportDialog } from '../CreateReportDialog/CreateReportDialog';
import { ExpandedTreeNodesContext } from './expandedNodesContext';
import { type TreeNodeType } from './treeUtils';

type ExpertReportTreeProps = {
  selectedReportId?: number;
  selectedType?: string;
  selectedBlockId?: number;
  onSelectReport: (id: number) => void;
  onSelectBlock: (reportId: number, blockId: number) => void;
  isDesktop?: boolean;
};

/** Рекурсивно собирает все ID блоков из дерева блоков */
const collectBlockIds = (blocks: BlockResponse[]): Set<number> => {
  const ids = new Set<number>();
  for (const block of blocks) {
    ids.add(block.id);
    if (block.children?.length) {
      for (const id of collectBlockIds(block.children)) {
        ids.add(id);
      }
    }
  }
  return ids;
};

export const ExpertReportTree: FC<ExpertReportTreeProps> = ({
  selectedReportId,
  selectedType,
  selectedBlockId,
  onSelectReport,
  onSelectBlock,
  isDesktop,
}) => {
  const { t } = useTranslation(['common', 'expertReport']);
  const queryClient = useQueryClient();
  const allowAdmin = useHasPermission('ADMIN_EXPERT_REPORT');
  const warnAboutPointsRule = usePointsRuleChangeWarning();
  const noticeResultsRecalculation = useResultsRecalculationNotice();
  const { data: reports, isLoading } = useExpertReports();
  const createReportMutation = useCreateExpertReport();
  const updateReportMutation = useUpdateExpertReport();
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedIds, setExpandedIds] = useState<string[]>([]);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [addingBlockForReportId, setAddingBlockForReportId] = useState<number | null>(null);

  // Вычисляем ID активного узла на основе текущего выбора
  const activeId = useMemo(() => {
    if (selectedType === 'block' && selectedBlockId && selectedReportId) {
      return `block-${selectedReportId}-${selectedBlockId}`;
    }
    if (selectedType === 'report' && selectedReportId) {
      return `report-${selectedReportId}`;
    }
    return '';
  }, [selectedReportId, selectedType, selectedBlockId]);

  // Автоматически раскрываем отчёт при навигации через URL
  const isAutoExpandDone = useRef(false);
  useEffect(() => {
    if (isAutoExpandDone.current || !selectedReportId) return;
    setExpandedIds((prev) => {
      const id = String(selectedReportId);
      return prev.includes(id) ? prev : [...prev, id];
    });
    isAutoExpandDone.current = true;
  }, [selectedReportId]);

  const handleCreateReport = (name: string) => {
    createReportMutation.mutate(
      { name, blocks: [] },
      {
        onSuccess: (data) => {
          setIsCreateDialogOpen(false);
          onSelectReport(data.id);
        },
      },
    );
  };

  const handleSelect = (nodeId: string, type: TreeNodeType) => {
    const parts = nodeId.split('-');

    if (type === 'report') {
      onSelectReport(Number(parts[1]));
    } else if (type === 'block') {
      onSelectBlock(Number(parts[1]), Number(parts[2]));
    }
  };

  const handleAddBlock = (reportId: number, parentBlockId?: number) => {
    const cachedReport = queryClient.getQueryData<ExpertReportResponse>(expertReportKeys.detail(reportId));
    if (!cachedReport) return;

    const existingBlockIds = collectBlockIds(cachedReport.blocks);
    const existingBlocks = flattenBlocks(cachedReport.blocks);
    const tempId = `temp-${Date.now()}`;
    const blockName = t('expertReport:blockName');

    setAddingBlockForReportId(reportId);

    updateReportMutation.mutate(
      {
        id: reportId,
        body: {
          name: cachedReport.name,
          predicate: toPredicateInput(cachedReport.predicate),
          blocks: [
            ...existingBlocks,
            {
              tempId,
              parentBlockTempId: null,
              parentBlockId: parentBlockId ?? null,
              name: blockName,
              isHeader: false,
              orderPosition: 0,
              isDeleted: false,
              rules: [],
            },
          ],
        },
      },
      {
        onSuccess: (updatedReport) => {
          setAddingBlockForReportId(null);
          // Новый блок меняет состав вложенных блоков у владельца правила расчёта, а само правило
          // за составом не меняется — смотрим, что с ним стало после сохранения
          warnAboutPointsRule([findPointsRuleOwnerOfChildren(updatedReport, parentBlockId)]);
          noticeResultsRecalculation(updatedReport);

          // Сразу обновляем кеш, чтобы BlockCard мог найти новый блок
          queryClient.setQueryData(expertReportKeys.detail(reportId), updatedReport);

          // Находим только что созданный блок, сравнивая ID
          const newBlockIds = collectBlockIds(updatedReport.blocks);
          for (const id of newBlockIds) {
            if (!existingBlockIds.has(id)) {
              // Убеждаемся, что отчёт раскрыт
              setExpandedIds((prev) => {
                const reportIdStr = String(reportId);
                return prev.includes(reportIdStr) ? prev : [...prev, reportIdStr];
              });
              onSelectBlock(reportId, id);
              return;
            }
          }
        },
        onError: () => {
          setAddingBlockForReportId(null);
        },
      },
    );
  };

  // Добавляет указанные ID в состояние раскрытых узлов (без дублирования)
  const handleExpandBlocks = (ids: string[]) => {
    setExpandedIds((prev) => {
      const toAdd = ids.filter((id) => !prev.includes(id));
      return toAdd.length ? [...prev, ...toAdd] : prev;
    });
  };

  // Раскрытые узлы для всего дерева: по ним узел любой глубины понимает, скрыты ли его блоки
  const expandedNodeIds = useMemo(() => new Set(expandedIds), [expandedIds]);

  // Фильтруем отчёты по поисковому запросу
  const filteredReports = useMemo(() => {
    if (!reports?.items) return [];
    const activeReports = reports.items.filter((r) => !r.isDeleted);
    if (!searchTerm.trim()) return activeReports;

    const term = searchTerm.toLowerCase();
    return activeReports.filter((report) => report.name.toLowerCase().includes(term));
  }, [reports?.items, searchTerm]);

  return (
    <>
      <Card className="flex h-full w-full flex-col gap-0 overflow-hidden pb-0">
        {/* Шапка */}
        <CardHeader className="flex h-auto flex-col gap-4 border-b border-border bg-card p-4">
          <div className="flex w-full items-center gap-4">
            <CardTitle className="text-base font-bold">{t('expertReport:title')}</CardTitle>
            {allowAdmin && (
              <Button variant="default" size="sm" className="ml-auto" onClick={() => setIsCreateDialogOpen(true)}>
                {t('common:add')}
              </Button>
            )}
          </div>

          <Search
            placeholder={t('expertReport:search')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </CardHeader>

        {/* Содержимое дерева */}
        <CardContent className="h-full overflow-hidden p-0">
          <ScrollArea className="h-full">
            <div className="p-2">
              {isLoading && (
                <div className="flex items-center justify-center p-8">
                  <Spinner className="size-6" />
                </div>
              )}

              {!isLoading && filteredReports.length === 0 && (
                <div className="p-4 text-center text-sm text-muted-foreground">
                  {searchTerm ? t('expertReport:noSearchResults') : t('expertReport:noReports')}
                </div>
              )}

              {!isLoading && filteredReports.length > 0 && (
                <ExpandedTreeNodesContext value={expandedNodeIds}>
                  <TreeList.Root value={expandedIds} onValueChange={setExpandedIds} activeNodeId={activeId}>
                    {filteredReports.map((report) => (
                      <ExpertReportTreeNodeWithData
                        key={report.id}
                        reportId={report.id}
                        reportName={report.name}
                        isExpanded={expandedIds.includes(String(report.id))}
                        expandedIds={expandedIds}
                        activeId={activeId}
                        isAddingBlock={addingBlockForReportId === report.id}
                        onSelect={handleSelect}
                        onAddBlock={handleAddBlock}
                        onExpandBlocks={handleExpandBlocks}
                        isDesktop={isDesktop}
                      />
                    ))}
                  </TreeList.Root>
                </ExpandedTreeNodesContext>
              )}
            </div>
          </ScrollArea>
        </CardContent>
      </Card>

      <CreateReportDialog
        isOpen={isCreateDialogOpen}
        onClose={() => setIsCreateDialogOpen(false)}
        onSubmit={handleCreateReport}
        isPending={createReportMutation.isPending}
      />
    </>
  );
};
